import useFetch from 'use-http';
import Button from 'components/UI/Button';
import Border from 'components/Border';
import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps';
import { useNavigate } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { $coordinate } from 'models/user';
import React, { useCallback } from 'react';
import { registrCheckin, registrCheckinAnother } from 'utils/helpers';
import { useKeycloak } from '@react-keycloak/web';

import styles from './styles.module.scss';

const waitingTime = process.env.REACT_APP_WAITING_TIME ? +process.env.REACT_APP_WAITING_TIME : 60;
const waitingMessage = process.env.REACT_APP_WAITING_MESSAGE
	? process.env.REACT_APP_WAITING_MESSAGE
	: 'Время ожидания истекло. Отсканируйте qr-код и подтвердите координаты повторно.';

interface UniversalMapsProps {
	mode: 'standard' | 'manual';
	// Для standard режима
	code?: string;
	// Для manual режима
	qrcode?: string;
	userId?: string;
	imageId?: string;
	// Fallback callback
	cb?: () => void;
}

export default function UniversalMaps({ mode, code, qrcode, userId, imageId, cb }: UniversalMapsProps) {
	const { post, error } = useFetch();
	const navigate = useNavigate();
	const coordinate = useUnit($coordinate);
	const { keycloak } = useKeycloak();
	const token = keycloak.token;
	const [loading, setLoading] = React.useState(false);
	const [localError, setLocalError] = React.useState<string | null>(null);
	const timerStarted = React.useRef(false);
	const timeoutId = React.useRef<ReturnType<typeof setTimeout>>();
	const lastVisibilityState = React.useRef<string>(document.visibilityState);
	const [mapError, setMapError] = React.useState<string | undefined>(undefined);
	const [mapKey, setMapKey] = React.useState(0);

	React.useEffect(() => {
		const handleVisibilityChange = () => {
			console.log('timer Visibility changed:', document.visibilityState);
			if (document.visibilityState === 'visible' && lastVisibilityState.current !== 'visible') {
				if (localStorage.getItem('eventOccurred') === 'true') {
					alert(waitingMessage);
					localStorage.removeItem('eventOccurred');
					navigate('/');
				}
			} else if (document.visibilityState !== 'visible') {
				lastVisibilityState.current = document.visibilityState;
			}
		};

		if (!timerStarted.current) {
			timeoutId.current = setTimeout(() => {
				console.log('timer Timeout triggered');
				localStorage.setItem('eventOccurred', 'true');
				alert(waitingMessage);
				navigate('/');
			}, waitingTime * 1000);
			console.log('timer Timeout set:', timeoutId.current);
			timerStarted.current = true;
		}

		document.addEventListener('visibilitychange', handleVisibilityChange);
		console.log('timer Event listener added');

		return () => {
			if (timeoutId.current) {
				clearTimeout(timeoutId.current);
				console.log('timer Timeout cleared:', timeoutId.current);
			}
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			console.log('timer Event listener removed');
		};
	}, [waitingMessage, waitingTime, navigate]);

	const handleRecalc = useCallback(async () => {
		try {
			setLoading(true);
			setMapKey(prev => prev + 1); // Принудительно перерендерить карту
		} finally {
			setLoading(false);
		}
	}, []);

	const handleGeoRight = useCallback(async () => {
		try {
			setLoading(true);
			setLocalError(null);

			if (mode === 'standard' && code) {
				await registrCheckin(post, code, token, coordinate);
			} else if (mode === 'manual' && qrcode && userId && imageId) {
				await registrCheckinAnother(post, qrcode, userId, Number(imageId), coordinate);
			} else {
				throw new Error('Недостаточно параметров для регистрации');
			}

			cb?.();
		} catch (err) {
			if (err instanceof Error) {
				setLocalError(err.message);
			} else {
				setLocalError('Неизвестная ошибка');
			}
			console.error(err);
		} finally {
			setLoading(false);
		}
	}, [mode, post, code, token, coordinate, qrcode, userId, imageId, navigate, cb]);

	const handleNotMyCoordinates = useCallback(async () => {
		if (mode === 'standard' && code) {
			navigate(`/photo/${code}`);
		} else if (mode === 'manual' && qrcode && userId && imageId) {
			try {
				setLoading(true);
				await registrCheckinAnother(post, qrcode, userId, Number(imageId));
			} catch (error) {
				console.error('Ошибка при регистрации чекина:', error);
			} finally {
				setLoading(false);
			}
			cb?.();
		}
	}, [mode, code, navigate, qrcode, userId, imageId, post, cb]);

	React.useEffect(() => {
		if (localError || error) {
			alert(localError || error?.message);
			navigate('/');
		}
	}, [localError, error, navigate]);

	const loadMap = React.useCallback(() => {
		setLoading(true);
		setMapError('');

		return (
			<YMaps key={mapKey}>
				<Map
					state={{ center: [coordinate?.latitude, coordinate?.longitude], zoom: 15 }}
					style={{ width: '90%', height: '80%', margin: 'auto' }}
				>
					<Placemark geometry={[coordinate?.latitude, coordinate?.longitude]} />
				</Map>
			</YMaps>
		);
	}, [coordinate, mapKey]);

	const mapComponent = React.useMemo(() => {
		try {
			return loadMap();
		} catch (err) {
			if (err instanceof Error) {
				setMapError(err.message);
			} else {
				setMapError('Неизвестная ошибка');
			}
			return null;
		} finally {
			setLoading(false);
		}
	}, [loadMap]);

	return (
		<Border>
			<div style={{ textAlign: 'center' }} className={styles['maps-wrap']}>
				{mapComponent}

				<div className="row">
					<div className="column-xs-12">
						<br />
						{loading && <div>Загрузка...</div>}
						{mapError && <div>Ошибка: {mapError}</div>}
					</div>
					<div className="column-xs-7">
						<Button variant="green" type="button" onClick={handleRecalc} disabled={loading}>
							Обновить координаты
						</Button>
					</div>
					<div className="column-xs-5">
						<Button variant="green" type="button" onClick={handleGeoRight} disabled={loading}>
							Я нахожусь тут
						</Button>
					</div>
					{mode === 'standard' && (
						<div className="column-xs-12">
							<br />
							<br />
							<br />
							<button
								type="button"
								onClick={loading ? () => {} : handleNotMyCoordinates}
								disabled={loading}
								style={{
									textDecoration: 'underline',
									color: loading ? '#999' : 'inherit',
									cursor: loading ? 'not-allowed' : 'pointer',
									opacity: loading ? 0.6 : 1,
									background: 'none',
									border: 'none',
									padding: 0,
									font: 'inherit',
								}}
							>
								Это не мои координаты
							</button>
						</div>
					)}
				</div>
			</div>
		</Border>
	);
}
